import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useToday } from '@resource-kit/controllers/react';
import { usePermissions } from '@hooks/useAuth';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { PERMISSIONS } from '@/permissions';
import { PHONE_DRIVER_PARAM, PHONE_VIEW, PHONE_VIEW_PARAM, ROUTES } from '@lib/routes';
import { DEFAULT_BOARD_RANGE_DAYS, boardWindow } from '../board/boardModel';
import { flowGateTime, hasDispatchWindow } from '../board/flowCopy';
import { flowStepModel } from '../board/flowSteps';
import { phaseOf } from '../board/rowCopy';
import { useBoardDays } from '../board/useBoardDays';
import { useDeliveryFlow } from '../hooks/useDeliveryFlow';
import { PhoneDriverStops } from './PhoneDriverStops';
import { PhoneInbox } from './PhoneInbox';
import { PhoneNow } from './PhoneNow';
import { PhoneSearch } from './PhoneSearch';
import { PhoneStageBar } from './PhoneStageBar';
import { phoneSteps } from './phoneModel';

/**
 * `/dispatch` on a phone (#2487): always today, whatever the URL's `date` /
 * `_stage` say. The board and flow reads are the desktop's own (same query
 * keys, same default window), read once here and handed down.
 */
export function PhoneDispatch() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const permissions = usePermissions();
  const today = useToday();
  const flow = useDeliveryFlow();
  const rangeDays = flow.data?.board_range_days ?? DEFAULT_BOARD_RANGE_DAYS;
  const board = useBoardDays(boardWindow(today, today, rangeDays), !flow.isLoading);
  const row = board.data?.find((day) => day.date === today);
  const nowMs = useMinuteNow();
  // No flow, no deadlines: the current step is unknown and the bar stays all todo.
  const phase = row && flow.data ? phaseOf(row, flowStepModel(flow.data), nowMs) : null;
  const packaging = flow.data ? flowGateTime(flow.data, 'packaging_cutoff') : null;
  const dispatch = flow.data ? flowGateTime(flow.data, 'dispatch_cutoff') : null;
  // A missing cutoff keeps today's behaviour: only a known inverted pair turns the window off.
  const windowed =
    packaging === null || dispatch === null || hasDispatchWindow(packaging, dispatch);
  // Without review:read the Inbox tab is hidden; the URL falls back to Now.
  const inbox =
    searchParams.get(PHONE_VIEW_PARAM) === PHONE_VIEW.inbox &&
    permissions.includes(PERMISSIONS.REVIEW_READ);
  // One driver's list (#2677); without a worker id the URL falls back to Now.
  const driverWorkerId =
    searchParams.get(PHONE_VIEW_PARAM) === PHONE_VIEW.driver
      ? searchParams.get(PHONE_DRIVER_PARAM) || null
      : null;
  // The search view (#2678): no stage bar, the search takes the whole page.
  const search = searchParams.get(PHONE_VIEW_PARAM) === PHONE_VIEW.search;

  if (search) {
    return (
      <div data-testid="phone-dispatch" className="flex min-h-full flex-col text-sm leading-[1.45]">
        <div className="flex flex-col gap-3 px-4 py-3.5">
          <PhoneSearch today={today} />
        </div>
      </div>
    );
  }

  if (driverWorkerId !== null) {
    return (
      <div data-testid="phone-dispatch" className="flex min-h-full flex-col text-sm leading-[1.45]">
        <div className="flex flex-col gap-3 px-4 py-3.5">
          <PhoneDriverStops workerId={driverWorkerId} today={today} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="phone-dispatch" className="flex min-h-full flex-col text-sm leading-[1.45]">
      <PhoneStageBar steps={phoneSteps(row, phase, windowed)} />
      <div className="flex flex-col gap-3 px-4 py-3.5">
        {inbox ? (
          <PhoneInbox />
        ) : (
          <>
            {/* The entry lives here, not in PhoneNow: it belongs to the view, not the progress. */}
            <Link
              to={ROUTES.dispatch.phoneSearch}
              data-testid="phone-search-entry"
              className="flex h-11 items-center gap-2 rounded-lg border border-border bg-card px-3 text-muted-foreground no-underline"
            >
              <Search aria-hidden className="size-4 shrink-0" />
              <span className="truncate">{t('dispatch.phone.search.entry')}</span>
            </Link>
            <PhoneNow today={today} flow={flow} board={board} row={row} />
          </>
        )}
      </div>
    </div>
  );
}
